import { useMemo } from 'react';

import {
  BaseLeadInOffer,
  BaseOffer,
  BaseRoomType,
  UseCheapestAlternativeOffer,
} from './types';
import { getRoomOffersSortedByCheapest } from './utils';

const useCheapestAlternativeOffer = <
  Offer extends BaseOffer,
  LeadInOffer extends BaseLeadInOffer,
  RoomType extends BaseRoomType,
>({
  packageOffers,
  procuredOfferRooms,
  roomTypes,
}: UseCheapestAlternativeOffer<Offer, LeadInOffer, RoomType>) => {
  const roomTypeLookup = useMemo(
    () => new Map(roomTypes?.map((roomType) => [roomType.id, roomType])),
    [roomTypes],
  );

  const leadInOfferLookup = useMemo(
    () =>
      new Map(
        procuredOfferRooms?.flatMap((room) =>
          room.leadInOffers.map((leadInOffer) => [leadInOffer.id, leadInOffer]),
        ),
      ),
    [procuredOfferRooms],
  );

  const sortedRoomOffers = useMemo(
    () => getRoomOffersSortedByCheapest(packageOffers),
    [packageOffers],
  );

  const cheapestPoRoomOffer = useMemo(
    () =>
      sortedRoomOffers?.find((roomOffer) =>
        leadInOfferLookup.has(roomOffer.hotelOffer.id),
      ),
    [leadInOfferLookup, sortedRoomOffers],
  );
  const cheapestRoomOffer = sortedRoomOffers?.[0];

  // Prioritise showing users PO offers before standard offers
  const cheapestAlternativeOffer = cheapestPoRoomOffer ?? cheapestRoomOffer;

  if (!cheapestAlternativeOffer) {
    return undefined;
  }

  const { roomTypeId, ...originalOffer } = cheapestAlternativeOffer;

  return {
    offer: originalOffer,
    leadInOffer: leadInOfferLookup.get(originalOffer.hotelOffer.id),
    roomType: roomTypeLookup.get(roomTypeId),
  };
};

export default useCheapestAlternativeOffer;
